/** Shared helpers for the instant demo on How It Works. */

export type Importance = "important" | "somewhat" | "not";

export interface DemoTopic {
  id: string;
  title: string;
  blurb: string | null;
  long_video_id: string | null;
  long_seconds: number | null;
  short_video_id: string | null;
  short_seconds: number | null;
  sort_order: number;
  is_opening: boolean;
  published: boolean;
}

/**
 * Accepts a Gumlet link (play.gumlet.io/embed/<id>, gumlet.tv/watch/<id>,
 * or any URL ending in the asset id) or a bare asset id and returns the id.
 * Returns "" when nothing usable is found.
 */
export function extractVideoId(input: string): string {
  const value = input.trim();
  if (!value) return "";
  if (/^[a-zA-Z0-9]{16,40}$/.test(value)) return value;
  const patterns = [
    /play\.gumlet\.io\/embed\/([a-zA-Z0-9]{16,40})/,
    /gumlet\.tv\/watch\/([a-zA-Z0-9]{16,40})/,
    /gumlet\.[a-z.]+\/(?:[\w-]+\/)*([a-zA-Z0-9]{16,40})(?:[/?#]|$)/,
  ];
  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match) return match[1];
  }
  return "";
}

export function gumletEmbedUrl(id: string): string {
  return `https://play.gumlet.io/embed/${id}?autoplay=false&preload=true&muted=true`;
}

/** 185 -> "3:05"; 3725 -> "1:02:05" */
export function formatRuntime(totalSeconds: number | null | undefined): string {
  if (!totalSeconds || totalSeconds <= 0) return "";
  const seconds = Math.round(totalSeconds);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** "3:05" or "1:02:05" or "185" -> seconds. Returns null when unparseable. */
export function parseRuntime(input: string): number | null {
  const value = input.trim();
  if (!value) return null;
  const parts = value.split(":").map((part) => Number(part));
  if (parts.some((part) => Number.isNaN(part))) return null;
  if (parts.length === 1) return Math.max(0, Math.round(parts[0]));
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
}

/** Rough "about 12 minutes" style label for a whole playlist. */
export function totalRuntimeLabel(totalSeconds: number): string {
  if (totalSeconds <= 0) return "";
  const minutes = Math.max(1, Math.round(totalSeconds / 60));
  return `${minutes} min`;
}
